import Phaser from "phaser";
import { TILE, COLS, ROWS, MAP_W_PX, MAP_H_PX } from "./map";

const MAP_W_PX_LOCAL = MAP_W_PX;
const MAP_H_PX_LOCAL = MAP_H_PX;

/**
 * One Heart — City of LimeZu renderer (Phase 12 full rebuild).
 *
 * 320×200 tile world. 28 themed buildings arranged in 4 rows around a
 * central plaza. 12 buildings use 6_Home_Designs PNGs as drop-in interior
 * backdrops; the remaining 16 use Theme_Sorter_Singles overlays + themed
 * Room_Builder floor tiles. 32 NPCs from Single_Characters_Legacy are
 * placed at their assigned destinations.
 *
 * Public API (stable for MainScene):
 *   - static preload(scene), new TilemapRenderer(scene), .render()
 *   - .walls (collision group), .interactionHooks
 *   - .setDoorLight(memberId, state)  no-op
 */

const FLOOR_KEY = "rb-floors";
const WALLS_KEY = "rb-walls";

const FLOOR_OUTDOOR = 159;        // cobblestone street
const FLOOR_GRASS = 168;           // park / grass alt
const FLOOR_PLAZA = 129;           // pink hex plaza tile

// Built-in floor index palette (verified via /tmp/floor_grid.png).
const F = {
  blue: 31, cream: 50, red: 54, teal: 58, white: 76,
  wood: 80, darkWood: 84, yellow: 121, pink: 129,
  beige: 109, brown: 94, redCheck: 38, parquet: 65,
  marble: 41, hexPink: 129, plank: 151, herring: 159,
  cobble: 168, chevronOrange: 173, lightGrey: 117,
};

const NPC = (name: string) =>
  `/art/limezu/2_Characters/Old/Single_Characters_Legacy/32x32/${name}_idle_anim_32x32.png`;

interface HomeDesign {
  layer1: string; layer2: string; layer3?: string;
  cols: number; rows: number;
}

const HOMES: Record<string, HomeDesign> = {
  generic:    { layer1: "/art/limezu/6_Home_Designs/Generic_Home_Designs/32x32/Generic_Home_1_Layer_1_32x32.png",
                layer2: "/art/limezu/6_Home_Designs/Generic_Home_Designs/32x32/Generic_Home_1_Layer_2_32x32.png",
                cols: 14, rows: 14 },
  condo:      { layer1: "/art/limezu/6_Home_Designs/Condominium_Designs/32x32/Condominium_Design_layer_1_32x32.png",
                layer2: "/art/limezu/6_Home_Designs/Condominium_Designs/32x32/Condominium_Design_layer_2_32x32.png",
                cols: 14, rows: 11 },
  condo2:     { layer1: "/art/limezu/6_Home_Designs/Condominium_Designs/32x32/Condominium_Design_2_layer_1_32x32.png",
                layer2: "/art/limezu/6_Home_Designs/Condominium_Designs/32x32/Condominium_Design_2_layer_2_32x32.png",
                cols: 14, rows: 6 },
  gym:        { layer1: "/art/limezu/6_Home_Designs/Gym_Designs/32x32/Gym_layer_1_32x32.png",
                layer2: "/art/limezu/6_Home_Designs/Gym_Designs/32x32/Gym_layer_2_32x32.png",
                cols: 19, rows: 15 },
  gym2:       { layer1: "/art/limezu/6_Home_Designs/Gym_Designs/32x32/Gym_2_layer_1_32x32.png",
                layer2: "/art/limezu/6_Home_Designs/Gym_Designs/32x32/Gym_2_layer_2_32x32.png",
                cols: 12, rows: 7 },
  iceCream:   { layer1: "/art/limezu/6_Home_Designs/Ice-Cream_Shop_Designs/32x32/Ice_Cream_Shop_Design_layer_1_32x32.png",
                layer2: "/art/limezu/6_Home_Designs/Ice-Cream_Shop_Designs/32x32/Ice_Cream_Shop_Design_layer_2_32x32.png",
                layer3: "/art/limezu/6_Home_Designs/Ice-Cream_Shop_Designs/32x32/Ice_Cream_Shop_Design_layer_3_32x32.png",
                cols: 12, rows: 10 },
  japanese:   { layer1: "/art/limezu/6_Home_Designs/Japanese_Interiors_Home_Designs/32x32/Japanese_Home_1_Layer_1_32x32.png",
                layer2: "/art/limezu/6_Home_Designs/Japanese_Interiors_Home_Designs/32x32/Japanese_Home_1_Layer_2_32x32.png",
                cols: 19, rows: 14 },
  museumE:    { layer1: "/art/limezu/6_Home_Designs/Museum_Designs/32x32/Museum_entrance_layer_1_32x32.png",
                layer2: "/art/limezu/6_Home_Designs/Museum_Designs/32x32/Museum_entrance_layer_2_32x32.png",
                cols: 16, rows: 22 },
  museumR1:   { layer1: "/art/limezu/6_Home_Designs/Museum_Designs/32x32/Museum_room_1_layer_1_32x32.png",
                layer2: "/art/limezu/6_Home_Designs/Museum_Designs/32x32/Museum_room_1_layer_2_32x32.png",
                cols: 20, rows: 17 },
  museumR4:   { layer1: "/art/limezu/6_Home_Designs/Museum_Designs/32x32/Museum_room_4_layer_1_32x32.png",
                layer2: "/art/limezu/6_Home_Designs/Museum_Designs/32x32/Museum_room_4_layer_2_32x32.png",
                cols: 16, rows: 10 },
  shooting:   { layer1: "/art/limezu/6_Home_Designs/Shooting_Range_Designs/32x32/Shooting_Range_Design_layer_1_32x32.png",
                layer2: "/art/limezu/6_Home_Designs/Shooting_Range_Designs/32x32/Shooting_Range_Design_layer_2_32x32.png",
                cols: 10, rows: 11 },
  tvStudio:   { layer1: "/art/limezu/6_Home_Designs/TV_Studio_Designs/32x32/Tv_Studio_Design_layer_1_32x32.png",
                layer2: "/art/limezu/6_Home_Designs/TV_Studio_Designs/32x32/Tv_Studio_Design_layer_2_32x32.png",
                layer3: "/art/limezu/6_Home_Designs/TV_Studio_Designs/32x32/Tv_Studio_Design_layer_3_32x32.png",
                cols: 11, rows: 10 },
};

interface BuildingDef {
  id: string;
  name: string;
  /** Tile coords of building's top-left corner. */
  col: number; row: number;
  /** If set: use this home design as the interior backdrop. */
  homeKey?: keyof typeof HOMES;
  /** If no home design: use a themed floor tile + plain rectangle. */
  cols?: number; rows?: number; floorIdx?: number;
  themeColor: number;
  npc?: { name: string; col: number; row: number };
  /** Theme_Sorter_Singles sprites to scatter inside this building.
   *  Format: { theme: theme folder name, indices: list of sprite numbers }.
   *  Sprites are placed in an automatic 4-column grid inside the building. */
  furniture?: { theme: string; indices: number[] };
  /** Animated objects (3_Animated_objects) to place — semantic name match.
   *  Each entry is a substring of the animated PNG filename. */
  animated?: string[];
}

// Theme folder + filename prefix (LimeZu convention has different "Singles" naming per theme).
const THEMES: Record<string, { folder: string; prefix: string }> = {
  livingRoom:    { folder: "2_Living_Room_Singles_32x32",          prefix: "Living_Room_Singles_32x32" },
  bathroom:      { folder: "3_Bathroom_Singles_32x32",             prefix: "Bathroom_Singles_32x32" },
  bedroom:       { folder: "4_Bedroom_Singles_32x32",              prefix: "Bedroom_Singles_32x32" },
  classroom:     { folder: "5_Classroom_and_Library_Singles_32x32",prefix: "Classroom_and_Library_Singles_32x32" },
  music:         { folder: "6_Music_and_Sport_32x32",              prefix: "Music_and_Sport_Singles_32x32" },
  art:           { folder: "7_Art_Singles_32x32",                  prefix: "Art_Singles_32x32" },
  gym:           { folder: "8_Gym_Singles_32x32",                  prefix: "Gym_Singles_32x32" },
  fishing:       { folder: "9_Fishing_Singles_32x32",              prefix: "Fishing_Singles_32x32" },
  birthday:      { folder: "10_Birthday_Party_Singles_32x32",      prefix: "Birthday_Party_Singles_32x32" },
  halloween:     { folder: "11_Halloween_Singles_32x32",           prefix: "Halloween_Singles_32x32" },
  kitchen:       { folder: "12_Kitchen_Singles_32x32",             prefix: "Kitchen_Singles_32x32" },
  conference:    { folder: "13_Conference_Hall_Singles_32x32",     prefix: "Conference_Hall_Singles_32x32" },
  basement:      { folder: "14_Basement_Singles_32x32",            prefix: "Basement_Singles_32x32" },
  christmas:     { folder: "15_Christmas_Singles_32x32",           prefix: "Christmas_SIngles_32x32" },
  grocery:       { folder: "16_Grocery_Store_Singles_32x32",       prefix: "Grocery_Store_Singles_32x32" },
  jail:          { folder: "18_Jail_Singles_32x32",                prefix: "Jail_Singles_32x32" },
  hospital:      { folder: "19_Hospital_SIngles_32x32",            prefix: "Hospital_Singles_32x32" },
  japanese:      { folder: "20_Japanese_Interiors_Singles_32x32",  prefix: "Japanese_Interiors_Singles_32x32" },
  clothing:      { folder: "21_Clothing_Store_Singles_32x32",      prefix: "Clothing_Store_Singles_32x32" },
  museum:        { folder: "22_Museum_Singles_32x32",              prefix: "Museum_Singles_32x32" },
  tvStudio:      { folder: "23_Television_and_Film_Studio_SIngles_32x32", prefix: "Television_and_FIlm_Studio_Singles_32x32" },
  iceCream:      { folder: "24_Ice_Cream_Shop_Singles_32x32",      prefix: "Ice_Cream_Shop_Singles_32x32" },
  shootingRange: { folder: "25_Shooting_Range_Singles_32x32",      prefix: "Shooting_Range_Singles_32x32" },
  condominium:   { folder: "26_Condominium_Singles_32x32",         prefix: "Condominium_Singles_32x32" },
};

function themeSprite(theme: string, idx: number): string {
  const t = THEMES[theme];
  return `/art/limezu/1_Interiors/32x32/Theme_Sorter_Singles_32x32/${t.folder}/${t.prefix}_${idx}.png`;
}

const ANIMATED_BASE = "/art/limezu/3_Animated_objects/32x32/spritesheets";

// Building grid layout: 4 rows of buildings around a central plaza.
// Plaza: cols 140-180, rows 80-120. Spawn at center (160, 100).
//
// `furniture` indices are bulk-sampled from the early-numbered sprites of
// each Theme_Sorter_Singles folder (typically 1-30 = the largest, most
// recognizable items per theme: tables, beds, counters, racks, etc.).
const BUILDINGS: BuildingDef[] = [
  // ─── Row 1 (north) — appearance shops ────────────────
  { id: "hatShop",   name: "Hat Shop",       col: 10,  row: 8,  cols: 14, rows: 12, floorIdx: F.yellow,    themeColor: 0xfbbf24, npc: { name: "Bouncer", col: 17, row: 21 },
    furniture: { theme: "clothing", indices: [3, 8, 14, 21, 30, 45, 60, 75, 90, 110] } },
  { id: "boutique",  name: "Boutique",       col: 28,  row: 8,  homeKey: "condo",    themeColor: 0xa78bfa, npc: { name: "Amelia",  col: 35, row: 21 } },
  { id: "salon",     name: "Hair Salon",     col: 48,  row: 8,  homeKey: "condo2",   themeColor: 0xec4899, npc: { name: "Lucy",    col: 55, row: 16 } },
  { id: "optical",   name: "Optical Center", col: 68,  row: 8,  cols: 12, rows: 10, floorIdx: F.cream,     themeColor: 0x06b6d4, npc: { name: "Doctor",  col: 74, row: 19 },
    furniture: { theme: "hospital", indices: [1, 5, 12, 18, 25, 32, 40, 50] } },
  { id: "bodyMod",   name: "Body Mod Studio",col: 84,  row: 8,  cols: 12, rows: 10, floorIdx: F.darkWood,  themeColor: 0xef4444, npc: { name: "Witch",   col: 90, row: 19 },
    furniture: { theme: "halloween", indices: [3, 8, 15, 22, 30, 45, 60, 80] } },
  { id: "accStand",  name: "Accessory Stand",col: 100, row: 8,  homeKey: "shooting", themeColor: 0x22d3ee, npc: { name: "Cleaner", col: 105, row: 20 } },
  { id: "techBaz",   name: "Tech Bazaar",    col: 114, row: 8,  homeKey: "tvStudio", themeColor: 0x3b82f6, npc: { name: "Edward",  col: 120, row: 19 } },
  { id: "swordSt",   name: "Sword Stand",    col: 130, row: 8,  cols: 10, rows: 10, floorIdx: F.parquet,   themeColor: 0x6366f1, npc: { name: "Samuel",  col: 135, row: 19 },
    furniture: { theme: "museum", indices: [3, 10, 18, 25, 35, 50, 70, 100] } },
  { id: "costHall",  name: "Costume Hall",   col: 200, row: 8,  cols: 14, rows: 12, floorIdx: F.pink,      themeColor: 0xec4899, npc: { name: "Santa",   col: 207, row: 21 },
    furniture: { theme: "clothing", indices: [5, 12, 20, 35, 50, 70, 95, 120, 150, 180] } },
  { id: "premade",   name: "Premade Looks",  col: 218, row: 8,  cols: 12, rows: 10, floorIdx: F.cream,     themeColor: 0xfbbf24, npc: { name: "Adam",    col: 224, row: 19 },
    furniture: { theme: "clothing", indices: [10, 25, 40, 55, 70, 85, 100, 130] } },
  { id: "kidWing",   name: "Kid Wing",       col: 234, row: 8,  cols: 12, rows: 10, floorIdx: F.blue,      themeColor: 0x10b981, npc: { name: "Kid",     col: 240, row: 19 },
    furniture: { theme: "bedroom", indices: [3, 12, 25, 40, 60, 80, 100, 130] } },

  // ─── Row 2 — entertainment (west) ──────────────────────
  { id: "musicHall", name: "Music Hall",     col: 8,   row: 36, cols: 18, rows: 16, floorIdx: F.darkWood,  themeColor: 0x10b981, npc: { name: "Roki",    col: 17, row: 53 },
    furniture: { theme: "music", indices: [1, 5, 12, 20, 30, 45, 60, 80, 100, 130] },
    animated: ["amplifier", "amplifier_big", "drum"] },
  { id: "artGall",   name: "Art Gallery",    col: 30,  row: 36, homeKey: "museumR4", themeColor: 0xa78bfa, npc: { name: "Ash",     col: 38, row: 44 } },
  { id: "library",   name: "Library",        col: 52,  row: 36, homeKey: "museumR4", themeColor: 0x06b6d4, npc: { name: "Molly",   col: 60, row: 47 } },
  { id: "museum",    name: "Museum",         col: 72,  row: 36, homeKey: "museumR1", themeColor: 0xfbbf24, npc: { name: "Cleaner", col: 82, row: 54 } },
  { id: "iceCream",  name: "Ice Cream",      col: 96,  row: 36, homeKey: "iceCream", themeColor: 0xfb7185, npc: { name: "Chef",    col: 102, row: 47 } },

  // ─── Row 3 — services (east) ───────────────────────────
  { id: "cafe",      name: "Café",           col: 200, row: 36, cols: 14, rows: 10, floorIdx: F.wood,      themeColor: 0xf97316, npc: { name: "Chef",    col: 207, row: 47 },
    furniture: { theme: "kitchen", indices: [3, 10, 20, 30, 50, 80, 120, 200] },
    animated: ["coffee_pot", "fridge_grey", "canteen_fridge_cake_1"] },
  { id: "restaurant",name: "Restaurant",     col: 218, row: 36, cols: 14, rows: 10, floorIdx: F.cream,     themeColor: 0xf97316, npc: { name: "Chef",    col: 225, row: 47 },
    furniture: { theme: "kitchen", indices: [5, 15, 25, 40, 60, 90, 150, 250] } },
  { id: "bakery",    name: "Bakery",         col: 236, row: 36, cols: 12, rows: 10, floorIdx: F.beige,     themeColor: 0xfb923c, npc: { name: "Chef",    col: 242, row: 47 },
    furniture: { theme: "kitchen", indices: [8, 18, 28, 55, 110, 180, 300] },
    animated: ["grocery_store_bakery_industrial_oven_up", "canteen_big_fridge_cake"] },
  { id: "grocery",   name: "Grocery Mart",   col: 252, row: 36, cols: 18, rows: 14, floorIdx: F.white,     themeColor: 0x22c55e, npc: { name: "Butcher", col: 261, row: 46 },
    furniture: { theme: "grocery", indices: [3, 10, 20, 35, 60, 90, 130, 180, 240, 320, 400] },
    animated: ["Shopping_Carts", "grocery_store_butchershop_cool_room_door"] },
  { id: "confCtr",   name: "Conference Ctr", col: 274, row: 36, cols: 14, rows: 12, floorIdx: F.lightGrey, themeColor: 0x64748b, npc: { name: "Conference", col: 281, row: 49 },
    furniture: { theme: "conference", indices: [1, 5, 10, 18, 25, 35, 50, 65] },
    animated: ["TV_reportage"] },
  { id: "partyHall", name: "Party Hall",     col: 292, row: 36, cols: 14, rows: 12, floorIdx: F.redCheck,  themeColor: 0xec4899, npc: { name: "Bob",     col: 299, row: 49 },
    furniture: { theme: "birthday", indices: [1, 5, 10, 15, 20, 28, 35, 42] } },

  // ─── Row 4 — residential / hangout (west) ────────────
  { id: "japanHouse",name: "Japanese House", col: 8,   row: 130, homeKey: "japanese", themeColor: 0xdc2626, npc: { name: "Roki",    col: 17, row: 145 } },
  { id: "den",       name: "The Den",        col: 30,  row: 130, homeKey: "generic",  themeColor: 0xfbbf24, npc: { name: "Bob",     col: 37, row: 145 } },
  { id: "loft",      name: "The Loft",       col: 48,  row: 130, cols: 14, rows: 12, floorIdx: F.parquet,   themeColor: 0xa78bfa, npc: { name: "Bruce",   col: 55, row: 143 },
    furniture: { theme: "bedroom", indices: [5, 15, 30, 50, 80, 120, 200, 300, 450] } },
  { id: "publicBath",name: "Public Bath",    col: 66,  row: 130, cols: 10, rows: 8,  floorIdx: F.teal,      themeColor: 0x22d3ee, npc: { name: "Old",     col: 71, row: 139 },
    furniture: { theme: "bathroom", indices: [3, 10, 25, 50, 75, 100, 130] },
    animated: ["bathtub", "bathroom_sink_2_new_3-10 loop", "bathroom_cabinet_blue_full"] },

  // ─── Row 5 — sports / exotic (east) ───────────────────
  { id: "gymA",      name: "Gym",            col: 200, row: 130, homeKey: "gym",      themeColor: 0xef4444, npc: { name: "Bruce",   col: 209, row: 146 } },
  { id: "gymB",      name: "Gym B",          col: 224, row: 130, homeKey: "gym2",     themeColor: 0xf97316, npc: { name: "Dan",     col: 230, row: 138 } },
  { id: "shootingR", name: "Shooting Range", col: 240, row: 130, cols: 14, rows: 12, floorIdx: F.lightGrey, themeColor: 0x6366f1, npc: { name: "Prisoner", col: 247, row: 143 },
    furniture: { theme: "shootingRange", indices: [1, 5, 10, 15, 20, 25] } },
  { id: "tvStudio2", name: "TV Studio",      col: 258, row: 130, cols: 14, rows: 12, floorIdx: F.beige,     themeColor: 0xfbbf24, npc: { name: "Pier",    col: 265, row: 143 },
    furniture: { theme: "tvStudio", indices: [1, 8, 18, 30, 50, 70] },
    animated: ["TV_reportage", "control_room_facebook_scrolling"] },
  { id: "policeSt",  name: "Police Station", col: 276, row: 130, cols: 14, rows: 12, floorIdx: F.white,     themeColor: 0x3b82f6, npc: { name: "Bouncer", col: 283, row: 143 },
    furniture: { theme: "jail", indices: [1, 10, 25, 50, 100, 150, 220, 300] } },
  { id: "hospital",  name: "Hospital",       col: 294, row: 130, cols: 16, rows: 14, floorIdx: F.white,     themeColor: 0x22d3ee, npc: { name: "Nurse",   col: 302, row: 145 },
    furniture: { theme: "hospital", indices: [3, 15, 30, 60, 100, 180, 280, 400, 500] },
    animated: ["TV_reportage_hospital", "hospital_xrayscreen_arm", "hospital_xraymachine_screen"] },

  // ─── Row 6 — bottom-row themed extras ────────────────────
  { id: "bar",       name: "Underground Bar",col: 8,   row: 168, cols: 14, rows: 12, floorIdx: F.darkWood,  themeColor: 0x6b21a8, npc: { name: "Alex",    col: 15, row: 181 },
    furniture: { theme: "basement", indices: [3, 12, 25, 50, 100, 150, 200] },
    animated: ["candle", "fishtank_orange"] },
  { id: "haunted",   name: "Haunted House",  col: 26,  row: 168, cols: 14, rows: 12, floorIdx: F.brown,     themeColor: 0xa855f7, npc: { name: "Zombie",  col: 33, row: 181 },
    furniture: { theme: "halloween", indices: [5, 20, 50, 80, 120, 160, 200] },
    animated: ["candle", "blood_altar"] },
  { id: "xmas",      name: "Christmas Pop-Up",col: 44, row: 168, cols: 12, rows: 10, floorIdx: F.redCheck,  themeColor: 0x16a34a, npc: { name: "Santa",   col: 50, row: 179 },
    furniture: { theme: "christmas", indices: [1, 8, 20, 40, 70, 100] },
    animated: ["Christmas_carousel", "Christmas_tree_lights", "Christmas_jack_in_the_box"] },
  { id: "sportsCtr", name: "Sports Center",  col: 60,  row: 168, cols: 14, rows: 12, floorIdx: F.lightGrey, themeColor: 0x06b6d4, npc: { name: "Rob",     col: 67, row: 181 },
    furniture: { theme: "gym", indices: [3, 12, 30, 50, 80, 130, 180] } },
  { id: "fishingP",  name: "Fishing Pier",   col: 296, row: 168, cols: 14, rows: 12, floorIdx: F.blue,      themeColor: 0x0ea5e9, npc: { name: "Fishmonger", col: 303, row: 181 },
    furniture: { theme: "fishing", indices: [1, 5, 12, 25, 40, 60] },
    animated: ["fishmonger_fish_tank", "fishtank_red"] },
];

// Plaza ambient walkers — extra NPCs not tied to a building.
const PLAZA_NPCS = [
  { name: "Adam",    col: 148, row: 95 },
  { name: "Alex",    col: 172, row: 95 },
  { name: "Lucy",    col: 148, row: 105 },
  { name: "Molly",   col: 172, row: 105 },
  { name: "Bob",     col: 158, row: 92 },
  { name: "Dan",     col: 162, row: 108 },
  { name: "Edward",  col: 145, row: 100 },
  { name: "Samuel",  col: 175, row: 100 },
  { name: "Pier",    col: 153, row: 113 },
  { name: "Rob",     col: 167, row: 113 },
];

const SPAWN_TILE = { col: 160, row: 100 };

/** Pick a door spritesheet key matching the building's character. */
function pickDoorKey(b: BuildingDef): string {
  const id = b.id;
  // Specific building → specific door
  if (id === "japanHouse") return "door-japanese_sliding_door";
  if (id === "hospital") return "door-hospital_door_blue";
  if (id === "policeSt") return "door-door_prison";
  if (id === "bar" || id === "haunted") return "door-door_2";
  if (id === "musicHall" || id === "swordSt") return "door-door_3";
  // Glass double doors for retail / public venues
  if (id === "cafe" || id === "restaurant" || id === "bakery" ||
      id === "grocery" || id === "optical" || id === "kidWing" ||
      id === "premade" || id === "iceCream" || id === "costHall" ||
      id === "publicBath" || id === "fishingP") return "door-door_glass_double";
  // Big doors for big venues
  const cols = b.homeKey ? HOMES[b.homeKey].cols : (b.cols ?? 14);
  if (cols >= 16) return "door-door_big_1";
  // Default wooden door
  return "door-door_1";
}

export class TilemapRenderer {
  scene: Phaser.Scene;
  walls!: Phaser.Physics.Arcade.StaticGroup;
  doorLights = new Map<string, Phaser.GameObjects.Graphics>();
  brand?: Phaser.GameObjects.Text;
  mytClock?: Phaser.GameObjects.Text;
  themeWashByRoom = new Map<string, Phaser.GameObjects.Rectangle>();
  interactionHooks: Array<{
    type: "sit" | "drink-coffee" | "drink-alcohol" | "toggle-light" | "open-closet" | "smoke";
    x: number; y: number; roomId?: string; ref?: unknown;
  }> = [];
  buildings = BUILDINGS;
  /** Sprite content dimensions per theme — loaded from sprite-dims.json.
   *  Used to skip oversize sprites that would overflow their grid cell. */
  spriteDims: Record<string, Record<string, { w: number; h: number; cw: number; ch: number; tw: number; th: number }>> = {};

  static preload(scene: Phaser.Scene): void {
    // Sprite dimension manifest — required for the no-half-crop rule.
    scene.load.json("sprite-dims", "/art/_meta/sprite-dims.json");
    scene.load.spritesheet(
      WALLS_KEY,
      "/art/limezu/1_Interiors/32x32/Room_Bulder_subfiles_32x32/Room_Builder_Walls_32x32.png",
      { frameWidth: 32, frameHeight: 32 },
    );
    scene.load.spritesheet(
      FLOOR_KEY,
      "/art/limezu/1_Interiors/32x32/Room_Bulder_subfiles_32x32/Room_Builder_Floors_32x32.png",
      { frameWidth: 32, frameHeight: 32 },
    );
    // Home designs
    const seen = new Set<string>();
    const queue = (path: string) => {
      if (seen.has(path)) return;
      seen.add(path);
      scene.load.image(path, path);
    };
    for (const b of BUILDINGS) {
      if (b.homeKey) {
        const h = HOMES[b.homeKey];
        queue(h.layer1);
        queue(h.layer2);
        if (h.layer3) queue(h.layer3);
      }
    }
    // NPCs
    const npcs = new Set<string>();
    for (const b of BUILDINGS) if (b.npc) npcs.add(b.npc.name);
    for (const n of PLAZA_NPCS) npcs.add(n.name);
    for (const name of npcs) {
      scene.load.spritesheet(`npc-${name}`, NPC(name), { frameWidth: 32, frameHeight: 32 });
    }
    // Themed furniture sprites for bare buildings
    const furnPaths = new Set<string>();
    for (const b of BUILDINGS) {
      if (b.furniture) for (const idx of b.furniture.indices) {
        furnPaths.add(themeSprite(b.furniture.theme, idx));
      }
    }
    // Living Room sprites for street lamps + bushes
    for (const idx of [13, 14, 15, 16, 17, 18, 47, 48, 49, 72, 76, 80, 84]) {
      furnPaths.add(themeSprite("livingRoom", idx));
    }
    for (const p of furnPaths) {
      scene.load.image(p, p);
    }
    // Animated objects
    const animNames = new Set<string>();
    for (const b of BUILDINGS) {
      if (b.animated) for (const n of b.animated) animNames.add(n);
    }
    for (const name of animNames) {
      const path = `${ANIMATED_BASE}/animated_${name}_32x32.png`;
      scene.load.spritesheet(`anim-${name}`, path, { frameWidth: 32, frameHeight: 32 });
    }
    // Door spritesheets — used as static frame-0 doors at bare-shell building
    // entrances. Each is 32×32 with several frames showing closed→open.
    const DOOR_KEYS = [
      "door_1", "door_2", "door_3", "door_big_1", "door_big_2",
      "door_glass_double", "door_glass_sliding", "door_prison",
      "japanese_sliding_door", "hospital_door_blue", "hospital_door_green",
    ];
    for (const k of DOOR_KEYS) {
      scene.load.spritesheet(
        `door-${k}`,
        `${ANIMATED_BASE}/animated_${k}_32x32.png`,
        { frameWidth: 32, frameHeight: 32 },
      );
    }
  }

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.walls = scene.physics.add.staticGroup();
    // Load sprite dimensions from the JSON cache (loaded in preload).
    const dims = scene.cache.json.get("sprite-dims");
    if (dims) this.spriteDims = dims;
  }

  render(): void {
    this.drawGrassBase();      // solid green outdoor base
    this.drawRoads();          // horizontal road strips between building rows
    this.drawSidewalks();      // sidewalk tiles bordering roads
    this.drawPlaza();
    for (const b of BUILDINGS) this.drawBuilding(b);
    for (const b of BUILDINGS) this.drawBuildingFurniture(b);
    for (const b of BUILDINGS) this.drawBuildingAnimated(b);
    for (const b of BUILDINGS) this.drawBuildingDoor(b);
    this.drawStreetLamps();
    this.drawBushesAndTrees();
    for (const b of BUILDINGS) if (b.npc) this.placeNPC(b.npc.name, b.npc.col, b.npc.row);
    for (const n of PLAZA_NPCS) this.placeNPC(n.name, n.col, n.row);
    this.drawOuterWalls();
  }

  /** Procedural grass base — generated via an offscreen HTML canvas, then
   *  registered as a Phaser texture and tiled across the map.
   *
   *  We use a raw 2D canvas (not Phaser Graphics+RenderTexture) because
   *  Graphics objects with setVisible(false) can fail to commit their fills
   *  through the WebGL pipeline, leaving the RT mostly empty. A plain
   *  canvas drawn pixel-by-pixel gives us a deterministic image we can
   *  hand directly to scene.textures.addCanvas. */
  private drawGrassBase(): void {
    const KEY = "grass-tex";
    if (!this.scene.textures.exists(KEY)) {
      const SIZE = 128;
      const canvas = document.createElement("canvas");
      canvas.width = SIZE;
      canvas.height = SIZE;
      const ctx = canvas.getContext("2d")!;
      // Base mid-green
      ctx.fillStyle = "#55833a";
      ctx.fillRect(0, 0, SIZE, SIZE);
      // Pixel noise — two tones of green, sparse but enough to break up flatness.
      const data = ctx.getImageData(0, 0, SIZE, SIZE);
      const pix = data.data;
      const setPx = (x: number, y: number, r: number, g: number, b: number) => {
        const i = (y * SIZE + x) * 4;
        pix[i] = r; pix[i + 1] = g; pix[i + 2] = b; pix[i + 3] = 255;
      };
      for (let i = 0; i < 260; i++) {
        const x = Math.floor(Math.random() * SIZE);
        const y = Math.floor(Math.random() * SIZE);
        if (i % 3 === 0) setPx(x, y, 0x4a, 0x75, 0x30);   // darker
        else setPx(x, y, 0x65, 0x95, 0x45);                // lighter
      }
      // Sparse 1px flowers
      const flowers = [
        [0xfd, 0xe0, 0x47],
        [0xff, 0xff, 0xff],
        [0xfb, 0xcf, 0xe8],
      ];
      for (let i = 0; i < 8; i++) {
        const f = flowers[i % flowers.length];
        const x = Math.floor(Math.random() * SIZE);
        const y = Math.floor(Math.random() * SIZE);
        setPx(x, y, f[0], f[1], f[2]);
      }
      ctx.putImageData(data, 0, 0);
      this.scene.textures.addCanvas(KEY, canvas);
    }
    const tile = this.scene.add.tileSprite(
      0, 0, MAP_W_PX_LOCAL, MAP_H_PX_LOCAL, KEY,
    ).setOrigin(0, 0).setDepth(-300);
    tile.setTileScale(1, 1);
  }

  /** Roads — procedural asphalt with lane markings. Drawn as Graphics
   *  rectangles to avoid the herringbone-wood floor frame the previous
   *  approach used. Road colour is dark slate; centre dashed white line. */
  private drawRoads(): void {
    const ROAD_ROWS = [22, 23, 24, 25, 56, 57, 58, 59, 76, 77, 78, 79, 122, 123, 124, 125, 162, 163, 164, 165, 184, 185, 186, 187];
    const PLAZA = { x0: 142, x1: 178, y0: 88, y1: 116 };
    const g = this.scene.add.graphics().setDepth(-200);
    g.fillStyle(0x2f3640, 1);  // asphalt dark
    // Group consecutive rows into single rects for fewer draws.
    let rIdx = 0;
    while (rIdx < ROAD_ROWS.length) {
      const startR = ROAD_ROWS[rIdx];
      let endR = startR;
      while (rIdx + 1 < ROAD_ROWS.length && ROAD_ROWS[rIdx + 1] === endR + 1) {
        endR = ROAD_ROWS[rIdx + 1]; rIdx++;
      }
      rIdx++;
      const y = startR * TILE;
      const h = (endR - startR + 1) * TILE;
      // Skip plaza band — but only the plaza columns, not the whole strip.
      const skipPlazaY = startR >= PLAZA.y0 && endR < PLAZA.y1;
      if (!skipPlazaY) {
        g.fillRect(0, y, COLS * TILE, h);
      } else {
        g.fillRect(0, y, PLAZA.x0 * TILE, h);
        g.fillRect(PLAZA.x1 * TILE, y, (COLS - PLAZA.x1) * TILE, h);
      }
      // Lane dashes — bright yellow centre line.
      const cy = y + h / 2;
      g.fillStyle(0xfde047, 0.9);
      const DASH = 24, GAP = 18;
      let dx = 6;
      while (dx < COLS * TILE) {
        if (dx >= PLAZA.x0 * TILE && dx < PLAZA.x1 * TILE && skipPlazaY) {
          dx = PLAZA.x1 * TILE + GAP;
          continue;
        }
        g.fillRect(dx, cy - 1, DASH, 2);
        dx += DASH + GAP;
      }
      g.fillStyle(0x2f3640, 1);
    }
    // Vertical connector road through the spine of the city.
    for (const c of [158, 159, 160, 161]) {
      // Two strips: above plaza and below plaza.
      g.fillRect(c * TILE, 0, TILE, PLAZA.y0 * TILE);
      g.fillRect(c * TILE, PLAZA.y1 * TILE, TILE, (ROWS - PLAZA.y1) * TILE);
    }
    // Vertical lane dashes — yellow stripe between columns 159 and 160.
    g.fillStyle(0xfde047, 0.9);
    const VDASH = 22, VGAP = 16;
    const VX = 160 * TILE - 1;
    let vy = 6;
    while (vy < ROWS * TILE) {
      if (vy >= PLAZA.y0 * TILE && vy < PLAZA.y1 * TILE) {
        vy = PLAZA.y1 * TILE + VGAP;
        continue;
      }
      g.fillRect(VX, vy, 2, VDASH);
      vy += VDASH + VGAP;
    }
  }

  /** Sidewalk strips — concrete grey with subtle paver-stone seams. */
  private drawSidewalks(): void {
    const SIDE_ROWS = [21, 26, 55, 60, 75, 80, 121, 126, 161, 166, 183, 188];
    const g = this.scene.add.graphics().setDepth(-180);
    g.fillStyle(0xb8b8b0, 1);
    for (const r of SIDE_ROWS) {
      g.fillRect(0, r * TILE, COLS * TILE, TILE);
    }
    // Vertical sidewalks
    const PLAZA_Y0 = 88, PLAZA_Y1 = 116;
    for (const c of [157, 162]) {
      g.fillRect(c * TILE, 0, TILE, PLAZA_Y0 * TILE);
      g.fillRect(c * TILE, PLAZA_Y1 * TILE, TILE, (ROWS - PLAZA_Y1) * TILE);
    }
    // Paver-stone seams — every 2 tiles, lighter horizontal hairline.
    g.fillStyle(0x9c9c92, 0.6);
    for (const r of SIDE_ROWS) {
      for (let c = 0; c < COLS; c += 2) {
        g.fillRect(c * TILE, r * TILE + TILE - 1, TILE, 1);
        g.fillRect(c * TILE + TILE - 1, r * TILE, 1, TILE);
      }
    }
    for (const c of [157, 162]) {
      for (let r = 0; r < PLAZA_Y0; r += 2) {
        g.fillRect(c * TILE, r * TILE + TILE - 1, TILE, 1);
      }
      for (let r = PLAZA_Y1; r < ROWS; r += 2) {
        g.fillRect(c * TILE, r * TILE + TILE - 1, TILE, 1);
      }
    }
  }

  /** Plaza centre — distinct floor + label. */
  private drawPlaza(): void {
    const PLAZA = { x0: 142, y0: 88, x1: 178, y1: 116 };
    for (let r = PLAZA.y0; r < PLAZA.y1; r++) {
      for (let c = PLAZA.x0; c < PLAZA.x1; c++) {
        this.scene.add
          .image(c * TILE + TILE / 2, r * TILE + TILE / 2, FLOOR_KEY, FLOOR_PLAZA)
          .setDepth(-150);
      }
    }
    const cx = ((PLAZA.x0 + PLAZA.x1) / 2) * TILE;
    const cy = ((PLAZA.y0 + PLAZA.y1) / 2) * TILE;
    const sign = this.scene.add.text(cx, cy, "♥ One Heart Plaza", {
      fontFamily: "Inter, system-ui, sans-serif",
      fontSize: "22px", fontStyle: "800", color: "#ffffff",
      backgroundColor: "rgba(255, 46, 136, 0.95)",
      padding: { x: 16, y: 8 },
    }).setOrigin(0.5, 0.5).setDepth(50);
    sign.setShadow(0, 0, "#000000", 8, true, true);
  }

  /** A single building: home design backdrop OR themed floor + collision shell. */
  private drawBuilding(b: BuildingDef): void {
    const x = b.col * TILE;
    const y = b.row * TILE;

    if (b.homeKey) {
      const h = HOMES[b.homeKey];
      // Backdrop fill — covers the building footprint with a neutral floor
      // tone so any transparent regions in the LimeZu home design layers
      // (intentional courtyards, gaps between rooms) don't show outdoor
      // grass underneath. The user reads "transparent gap" as "missing wall".
      const backdrop = this.scene.add.rectangle(
        x + (h.cols * TILE) / 2,
        y + (h.rows * TILE) / 2,
        h.cols * TILE,
        h.rows * TILE,
        0xc8c4b8, 1,  // light stone-grey, like museum floor
      );
      backdrop.setDepth(-110 + b.row * 0.001);
      if (this.scene.textures.exists(h.layer1)) {
        this.scene.add.image(x, y, h.layer1).setOrigin(0, 0).setDepth(-100 + b.row * 0.001);
      }
      if (this.scene.textures.exists(h.layer2)) {
        this.scene.add.image(x, y, h.layer2).setOrigin(0, 0).setDepth(-50 + b.row * 0.001);
      }
      if (h.layer3 && this.scene.textures.exists(h.layer3)) {
        this.scene.add.image(x, y, h.layer3).setOrigin(0, 0).setDepth(-20 + b.row * 0.001);
      }
      // Collision around home design footprint with door gap on south.
      this.addBuildingCollision(x, y, h.cols, h.rows);
      // Sign
      this.drawSign(x + (h.cols / 2) * TILE, y + (h.rows + 1.4) * TILE, b.name, b.themeColor);
    } else {
      // Themed floor rectangle building — for the 16 buildings without
      // pre-built home designs. Floor tiles + 4 wall sides + name sign.
      const cols = b.cols ?? 14;
      const rows = b.rows ?? 12;
      const floor = b.floorIdx ?? F.cream;
      // Interior floor
      for (let r = 1; r < rows - 1; r++) {
        for (let c = 1; c < cols - 1; c++) {
          this.scene.add
            .image(x + c * TILE + TILE / 2, y + r * TILE + TILE / 2, FLOOR_KEY, floor)
            .setDepth(-90);
        }
      }
      // Walls (Room_Builder_Walls_32x32 first row: NW=0, N=1, NE=2, W=32, E=34, SW=64, S=65, SE=66)
      const drawW = (col: number, row: number, frame: number) => {
        this.scene.add
          .image(x + col * TILE + TILE / 2, y + row * TILE + TILE / 2, WALLS_KEY, frame)
          .setDepth((y + row * TILE) * 0.001 + 5);
      };
      drawW(0, 0, 0); for (let c = 1; c < cols - 1; c++) drawW(c, 0, 1); drawW(cols - 1, 0, 2);
      for (let r = 1; r < rows - 1; r++) { drawW(0, r, 32); drawW(cols - 1, r, 34); }
      drawW(0, rows - 1, 64);
      const doorCol = Math.floor(cols / 2);
      for (let c = 1; c < cols - 1; c++) {
        if (c === doorCol || c === doorCol - 1) continue;
        drawW(c, rows - 1, 65);
      }
      drawW(cols - 1, rows - 1, 66);
      // Collision rect with door gap
      this.addBuildingCollision(x, y, cols, rows);
      // Sign
      this.drawSign(x + (cols / 2) * TILE, y + (rows + 1.4) * TILE, b.name, b.themeColor);
    }
  }

  /** Place themed furniture sprites inside a bare-shell building.
   *
   *  Discipline rule (from feedback_no_half_crop_delivery): NEVER place a
   *  sprite without checking its dimensions first. We pre-loaded
   *  sprite-dims.json in preload(); this method now refuses to draw any
   *  sprite that would overflow its grid cell. Oversize sprites are
   *  silently skipped so the rendered building never has clipped furniture.
   *
   *  Cell layout: 3-col × 3-row grid inside the building (skipping wall
   *  rows). Each cell ≈ (cols-4)/3 wide × (rows-4)/3 tall, in tile units
   *  → typically 3×3 tiles = 96×96 px. Sprites larger than that get skipped.
   */
  private drawBuildingFurniture(b: BuildingDef): void {
    if (b.homeKey || !b.furniture) return;
    const cols = b.cols ?? 14;
    const rows = b.rows ?? 12;
    const interiorCols = cols - 2;  // skip 1 tile each side for walls
    const interiorRows = rows - 2;
    const COLS_GRID = 3;
    const ROWS_GRID = 3;
    const cellW = Math.floor(interiorCols / COLS_GRID);          // tiles
    const cellH = Math.floor(interiorRows / ROWS_GRID);          // tiles
    const cellWpx = cellW * TILE;
    const cellHpx = cellH * TILE;
    const dimsTheme = (this.spriteDims[b.furniture.theme] || {}) as Record<string, { cw: number; ch: number }>;

    let placed = 0;
    for (const idx of b.furniture.indices) {
      if (placed >= COLS_GRID * ROWS_GRID) break;
      const row = Math.floor(placed / COLS_GRID);
      const col = placed % COLS_GRID;
      const path = themeSprite(b.furniture.theme, idx);
      if (!this.scene.textures.exists(path)) continue;

      // Dimension check — skip if sprite content overflows the cell.
      const dim = dimsTheme[String(idx)];
      if (dim) {
        if (dim.cw > cellWpx - 4 || dim.ch > cellHpx + TILE) {
          // Skip without advancing `placed` so the next index gets the slot.
          continue;
        }
      }

      // Cell centre (x), bottom (y) — so sprite feet anchor at cell bottom.
      const cellLeft = b.col + 1 + col * cellW;
      const cellTop  = b.row + 1 + row * cellH;
      const x = (cellLeft + cellW / 2) * TILE;
      const y = (cellTop + cellH) * TILE;          // bottom of cell

      // Drop shadow under the sprite — fakes ground contact.
      const shadowW = Math.min(cellWpx - 8, dim?.cw ?? 28);
      this.scene.add.ellipse(x, y - 2, shadowW * 0.85, 6, 0x000000, 0.3)
        .setDepth(y * 0.01 + 0.4);

      const img = this.scene.add.image(x, y, path);
      img.setOrigin(0.5, 1);
      img.setDepth(y * 0.01 + 1);
      placed++;
    }
  }

  /** Place animated objects (TVs, coffee makers, x-rays, etc.) inside a
   *  building. Animated objects are spritesheet PNGs with auto-cycling
   *  frame animation. */
  private drawBuildingAnimated(b: BuildingDef): void {
    if (!b.animated || b.animated.length === 0) return;
    const cols = b.cols ?? 14;
    const rows = b.rows ?? 12;
    let placed = 0;
    for (const name of b.animated) {
      const key = `anim-${name}`;
      if (!this.scene.textures.exists(key)) continue;
      // Place along the back wall, evenly distributed
      const slot = placed;
      const x = (b.col + 3 + slot * 4) * TILE;
      const y = (b.row + 3) * TILE;
      const sprite = this.scene.add.sprite(x, y, key, 0);
      sprite.setOrigin(0.5, 0.5);
      sprite.setDepth(y * 0.01 + 1);
      const animKey = `${key}-loop`;
      if (!this.scene.anims.exists(animKey)) {
        try {
          const tex = this.scene.textures.get(key);
          const total = tex.frameTotal - 1;
          this.scene.anims.create({
            key: animKey,
            frames: this.scene.anims.generateFrameNumbers(key, { start: 0, end: Math.min(total, 7) }),
            frameRate: 6, repeat: -1,
          });
        } catch { /* skip */ }
      }
      try { sprite.play(animKey); } catch { /* skip */ }
      placed++;
      if (placed >= 4) break;
    }
  }

  /** Visible door at each building's south-wall door gap.
   *  - Home-design buildings: door is already painted in the LimeZu artwork,
   *    so we just add a soft glow under it as an "enter here" hint.
   *  - Bare-shell buildings: place a real LimeZu animated_door spritesheet
   *    (frame 0 = closed) at the door gap so the entrance is visible. */
  private drawBuildingDoor(b: BuildingDef): void {
    const home = b.homeKey ? HOMES[b.homeKey] : null;
    const cols = home ? home.cols : (b.cols ?? 14);
    const rows = home ? home.rows : (b.rows ?? 12);
    const doorCenter = Math.floor(cols / 2);
    const doorX = (b.col + doorCenter) * TILE;
    const doorY = (b.row + rows) * TILE;

    // Pick a door variant suitable for the building.
    const doorKey = pickDoorKey(b);

    if (!home && this.scene.textures.exists(doorKey)) {
      // 32×32 door — bottom-aligned to the south wall so it sits in the gap.
      // Frame 0 is the closed (resting) state.
      const door = this.scene.add.image(doorX - TILE / 2, doorY, doorKey, 0);
      door.setOrigin(0, 1);
      door.setDepth(doorY * 0.01 + 1);
    }

    // Soft warm glow under the door — subtle "entry" hint for both building types.
    const glow = this.scene.add.ellipse(doorX, doorY + 6, TILE * 2.4, 10, 0xfde68a, 0.45);
    glow.setDepth(doorY * 0.01 + 0.05);
    this.scene.tweens.add({
      targets: glow,
      alpha: { from: 0.3, to: 0.6 },
      duration: 1600,
      yoyo: true,
      repeat: -1,
    });
  }

  /** Procedural streetlamps — Graphics-drawn lamp posts placed along
   *  sidewalk rows only. Each lamp has a dark pole, brass head, and a
   *  pulsing warm-yellow halo. LimeZu has no outdoor lamp sprite, so we
   *  draw our own. */
  private drawStreetLamps(): void {
    const SIDE_ROWS = [21, 26, 55, 60, 75, 80, 121, 126, 161, 166, 183, 188];
    const ROAD_ROWS_SET = new Set(
      [22, 23, 24, 25, 56, 57, 58, 59, 76, 77, 78, 79, 122, 123, 124, 125, 162, 163, 164, 165, 184, 185, 186, 187],
    );
    for (const r of SIDE_ROWS) {
      // Skip if this row is also a road row (defensive; never true given
      // the static lists, but a future change shouldn't double-place).
      if (ROAD_ROWS_SET.has(r)) continue;
      for (let c = 6; c < COLS - 6; c += 12) {
        // Skip plaza band columns
        if (c >= 142 && c < 178 && r >= 88 && r < 116) continue;
        this.drawOneStreetLamp(c * TILE + TILE / 2, r * TILE + TILE - 4);
      }
    }
    // Vertical sidewalks (col 157 and 162) — lamp every 12 rows.
    for (const c of [157, 162]) {
      for (let r = 6; r < ROWS - 6; r += 12) {
        if (r >= 88 && r < 116) continue;
        this.drawOneStreetLamp(c * TILE + TILE / 2, r * TILE + TILE - 4);
      }
    }
  }

  /** Draw a single lamp post at world (x, y) where y is the BASE of the pole. */
  private drawOneStreetLamp(x: number, y: number): void {
    const g = this.scene.add.graphics();
    g.setPosition(x, y);
    // Pole (dark slate)
    g.fillStyle(0x1f2937, 1).fillRect(-1.5, -22, 3, 22);
    // Cap
    g.fillStyle(0x111827, 1).fillRect(-3, -24, 6, 2);
    // Lamp head — brass-yellow rounded rect
    g.fillStyle(0xeab308, 1).fillRect(-5, -30, 10, 6);
    g.fillStyle(0xfacc15, 1).fillRect(-4, -29, 8, 3);
    // Glass front highlight
    g.fillStyle(0xfffbeb, 0.7).fillRect(-3, -28, 2, 1);
    g.setDepth(y * 0.01 + 2);
    // Warm halo glow — pulsing
    const halo = this.scene.add.ellipse(x, y - 26, 56, 28, 0xfde68a, 0.22);
    halo.setDepth(y * 0.01 + 0.4);
    this.scene.tweens.add({
      targets: halo,
      alpha: { from: 0.16, to: 0.28 },
      duration: 1800 + Math.random() * 600,
      yoyo: true,
      repeat: -1,
    });
  }

  /** Procedural trees and bushes scattered around buildings. Drawn via
   *  Graphics (no LimeZu outdoor assets exist). Deterministic placement
   *  per building so layout is stable across reloads. */
  private drawBushesAndTrees(): void {
    const ROAD_SET = new Set([22, 23, 24, 25, 56, 57, 58, 59, 76, 77, 78, 79, 122, 123, 124, 125, 162, 163, 164, 165, 184, 185, 186, 187]);
    const SIDE_SET = new Set([21, 26, 55, 60, 75, 80, 121, 126, 161, 166, 183, 188]);
    const occupied = (c: number, r: number) => {
      if (ROAD_SET.has(r) || SIDE_SET.has(r)) return true;
      if (c >= 142 && c < 178 && r >= 88 && r < 116) return true; // plaza
      // Inside a building footprint?
      for (const b of BUILDINGS) {
        const w = b.homeKey ? HOMES[b.homeKey].cols : (b.cols ?? 14);
        const h = b.homeKey ? HOMES[b.homeKey].rows : (b.rows ?? 12);
        if (c >= b.col - 1 && c <= b.col + w && r >= b.row - 1 && r <= b.row + h) return true;
      }
      return false;
    };
    for (const b of BUILDINGS) {
      const w = b.homeKey ? HOMES[b.homeKey].cols : (b.cols ?? 14);
      const h = b.homeKey ? HOMES[b.homeKey].rows : (b.rows ?? 12);
      // Hash-based per-building seed so layout is stable.
      const seed = b.col * 31 + b.row * 7;
      const candidates = [
        { c: b.col - 2,     r: b.row - 1, kind: "tree" },
        { c: b.col + w + 1, r: b.row - 1, kind: "tree" },
        { c: b.col - 2,     r: b.row + h + 2, kind: "bush" },
        { c: b.col + w + 1, r: b.row + h + 2, kind: "bush" },
        { c: b.col - 1,     r: b.row + Math.floor(h / 2), kind: "bush" },
        { c: b.col + w,     r: b.row + Math.floor(h / 2), kind: "bush" },
      ];
      for (let i = 0; i < candidates.length; i++) {
        const { c, r, kind } = candidates[i];
        if (c < 1 || c >= COLS - 1 || r < 1 || r >= ROWS - 1) continue;
        if (occupied(c, r)) continue;
        const x = c * TILE + TILE / 2;
        const y = r * TILE + TILE - 4;
        const variant = (seed + i) % 3;
        if (kind === "tree") this.drawTree(x, y, variant);
        else this.drawBush(x, y, variant);
      }
    }
  }

  private drawTree(x: number, y: number, variant: number): void {
    const g = this.scene.add.graphics().setPosition(x, y);
    // Shadow
    g.fillStyle(0x000000, 0.25).fillEllipse(0, 0, 26, 8);
    // Trunk
    g.fillStyle(0x6b3f1f, 1).fillRect(-3, -16, 6, 16);
    g.fillStyle(0x4a2a14, 1).fillRect(-3, -16, 1, 16);
    // Crown — overlapping circles for foliage
    const crownColors = [0x2d6a3a, 0x3d8b4a, 0x4d8c3a];
    const main = crownColors[variant % crownColors.length];
    const lite = 0x7cba5a;
    g.fillStyle(main, 1).fillCircle(0, -22, 13);
    g.fillStyle(main, 1).fillCircle(-7, -19, 9);
    g.fillStyle(main, 1).fillCircle(7, -19, 9);
    g.fillStyle(main, 1).fillCircle(0, -28, 8);
    // Highlight
    g.fillStyle(lite, 0.7).fillCircle(-4, -25, 4);
    g.setDepth(y * 0.01 + 1);
  }

  private drawBush(x: number, y: number, variant: number): void {
    const g = this.scene.add.graphics().setPosition(x, y);
    g.fillStyle(0x000000, 0.22).fillEllipse(0, 0, 18, 5);
    const colors = [0x3d8b4a, 0x4d9c5a, 0x336e3b];
    const c = colors[variant % colors.length];
    g.fillStyle(c, 1).fillCircle(0, -6, 8);
    g.fillStyle(c, 1).fillCircle(-5, -4, 6);
    g.fillStyle(c, 1).fillCircle(5, -4, 6);
    g.fillStyle(0x7cba5a, 0.8).fillCircle(-2, -8, 3);
    // Optional flower dot
    if (variant === 1) g.fillStyle(0xfca5a5, 1).fillCircle(2, -6, 1.5);
    if (variant === 2) g.fillStyle(0xfde047, 1).fillCircle(-1, -8, 1.5);
    g.setDepth(y * 0.01 + 1);
  }

  private addBuildingCollision(x: number, y: number, cols: number, rows: number): void {
    const T = TILE;
    const right = x + cols * T;
    const bottom = y + rows * T;
    // North wall
    this.addWall(x, y, cols * T, 4);
    // West
    this.addWall(x, y, 4, rows * T);
    // East
    this.addWall(right - 4, y, 4, rows * T);
    // South — split with 2-tile door gap centred.
    const doorCenter = Math.floor(cols / 2);
    const doorStartX = x + (doorCenter - 1) * T;
    const doorEndX = x + (doorCenter + 1) * T;
    if (doorStartX > x) this.addWall(x, bottom - 4, doorStartX - x, 4);
    if (right > doorEndX) this.addWall(doorEndX, bottom - 4, right - doorEndX, 4);
  }

  private addWall(x: number, y: number, w: number, h: number): void {
    const zone = this.scene.add.zone(x + w / 2, y + h / 2, w, h);
    this.scene.physics.add.existing(zone, true);
    this.walls.add(zone);
  }

  private drawSign(x: number, y: number, text: string, color: number): void {
    const sign = this.scene.add.text(x, y, text, {
      fontFamily: "Inter, system-ui, sans-serif",
      fontSize: "13px", fontStyle: "700",
      color: "#" + color.toString(16).padStart(6, "0"),
      backgroundColor: "rgba(15, 12, 30, 0.92)",
      padding: { x: 7, y: 3 },
    }).setOrigin(0.5, 0.5).setDepth(200);
    sign.setShadow(0, 0, "#000000", 5, true, true);
  }

  private placeNPC(name: string, col: number, row: number): void {
    const key = `npc-${name}`;
    if (!this.scene.textures.exists(key)) return;
    const x = col * TILE + TILE / 2;
    const y = row * TILE + TILE / 2;
    // NPC sprite at 2× to match the player avatar (which renders at scale 2).
    // Without this NPCs look like tiny dark blobs next to the much larger player.
    const NPC_SCALE = 2;
    this.scene.add.ellipse(x, y + 22, 28, 8, 0x000000, 0.35).setDepth(y * 0.01 + 0.5);
    const sprite = this.scene.add.sprite(x, y, key, 0)
      .setOrigin(0.5, 0.5)
      .setScale(NPC_SCALE);
    sprite.setDepth(y * 0.01 + 1);
    const animKey = `${key}-idle`;
    if (!this.scene.anims.exists(animKey)) {
      try {
        this.scene.anims.create({
          key: animKey,
          frames: this.scene.anims.generateFrameNumbers(key, { start: 0, end: 3 }),
          frameRate: 4, repeat: -1,
        });
      } catch { /* anim might already exist */ }
    }
    try { sprite.play(animKey); } catch { /* skip */ }
    const label = this.scene.add.text(x, y - 38, name, {
      fontFamily: "Inter, system-ui, sans-serif",
      fontSize: "11px", color: "#ffffff",
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      padding: { x: 5, y: 2 },
    }).setOrigin(0.5, 1).setDepth(220);
    label.setShadow(0, 0, "#000000", 4, true, true);
  }

  private drawOuterWalls(): void {
    const T = TILE;
    const rects = [
      { x: 0, y: 0, w: COLS * T, h: T },
      { x: 0, y: (ROWS - 1) * T, w: COLS * T, h: T },
      { x: 0, y: 0, w: T, h: ROWS * T },
      { x: (COLS - 1) * T, y: 0, w: T, h: ROWS * T },
    ];
    for (const r of rects) {
      const zone = this.scene.add.zone(r.x + r.w / 2, r.y + r.h / 2, r.w, r.h);
      this.scene.physics.add.existing(zone, true);
      this.walls.add(zone);
    }
  }

  setDoorLight(_memberId: string, _state: "off" | "online" | "in-claude" | "occupied"): void {
    // intentionally empty
  }

  static getSpawn(): { x: number; y: number } {
    return { x: SPAWN_TILE.col * TILE + TILE / 2, y: SPAWN_TILE.row * TILE + TILE / 2 };
  }

  /** Snapshot of city geometry, sized for the Minimap HUD. */
  getMinimapConfig() {
    return {
      mapTileCols: COLS,
      mapTileRows: ROWS,
      tilePx: TILE,
      buildings: BUILDINGS.map((b) => ({
        col: b.col,
        row: b.row,
        cols: b.homeKey ? HOMES[b.homeKey].cols : (b.cols ?? 14),
        rows: b.homeKey ? HOMES[b.homeKey].rows : (b.rows ?? 12),
        themeColor: b.themeColor,
        name: b.name,
      })),
      roadRows: [22, 23, 24, 25, 56, 57, 58, 59, 76, 77, 78, 79, 122, 123, 124, 125, 162, 163, 164, 165, 184, 185, 186, 187],
      sideRows: [21, 26, 55, 60, 75, 80, 121, 126, 161, 166, 183, 188],
      plaza: { x0: 142, y0: 88, x1: 178, y1: 116 },
      vRoadCols: [158, 159, 160, 161],
    };
  }
}
